import React, { useState, useEffect, useMemo } from 'react';
import { getSections, getMenuItems, deleteMenuItem, editMenuItem, addMenuItem, addSection, deleteSection, updateSection } from '../../api/menu-api.js';
import ItemModal from './ItemModal';
import SectionModal from './SectionModal';
import SearchBar from './SearchBar';
import SectionHeader from './SectionHeader';
import MenuItemCard from './MenuItemCard';
import { TbClipboardOff } from "react-icons/tb";

const MenuManagement = () => {
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showItemModal, setShowItemModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);

  const fetchData = async () => {
    try {
      const [sectionsData, itemsData] = await Promise.all([
        getSections(),
        getMenuItems()
      ]);
      setSections(sectionsData);
      setItems(itemsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const menuData = useMemo(() => {

    const grouped = {};
    sections.forEach(section => {
      grouped[section?.id] = {
        id: section.id,
        name: section.name,
        name_arabic: section.name_arabic,
        items: items.filter(item => item.section_id === section.id),
      };
    });
    return Object.values(grouped);
  }, [sections, items]);

  const handleDeleteItem = async (itemId) => {
    try {
      await deleteMenuItem(itemId);
      setItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      alert('Delete failed: ' + error.message);
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      let savedItem;
      if (itemData.id) {
        savedItem = await editMenuItem(itemData.id, itemData);
        setItems(prev => prev.map(item => 
          item.id === savedItem.id ? { ...savedItem, section_id: savedItem.section_id } : item
        ));
      } else {
        if (itemData.id === "") {
          delete itemData.id
        }
        savedItem = await addMenuItem(itemData);
        setItems(prev => [...prev, { 
          ...savedItem,
          section_id: savedItem.section_id
        }]);
      }
      
      setShowItemModal(false);
      setEditingItem(null);
      return savedItem;
    } catch (error) {
      alert(`Operation failed: ${error.message}`);
    }
  };

  const handleSaveSection = async (sectionData) => {
    try {
      if (sectionData.id) {
        const updatedSection = await updateSection(sectionData.id, sectionData);
        setSections(prev => prev.map(s => s.id === updatedSection.id ? updatedSection : s));
      } else {
        const newSection = await addSection(sectionData);
        console.log(newSection);
        
        setSections(prev => [...prev, newSection]);
      }
      setShowSectionModal(false);
    } catch (error) {
      alert(`Section operation failed: ${error.message}`);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      await deleteSection(sectionId);
      setSections(prev => prev.filter(s => s.id !== sectionId));
      setItems(prev => prev.filter(item => item.section_id !== sectionId));
    } catch (error) {
      alert('Delete failed: ' + error.message);
    }
  };

  // Filtered menu data with search
  const filteredMenuData = menuData.map(section => ({
    ...section,
    items: section.items.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name_arabic.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description_arabic?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.items.length > 0 || searchTerm === '');

  return (
    <div className="container mx-auto">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gold-dark">Menu Management</h1>
        <div className="flex gap-4">
          <button
            className="bg-gold-dark hover:bg-gold text-white px-4 py-2 rounded transition-colors duration-200"
            onClick={() => {
              setEditingItem(null);
              setShowItemModal(true);
            }}
          >
            Add New Item
          </button>
          <button
            className="bg-gold-dark hover:bg-gold text-white px-4 py-2 rounded transition-colors duration-200"
            onClick={() => {
              setEditingSection(null);
              setShowSectionModal(true);
            }}
          >
            Add New Section
          </button>
        </div>
      </div>
      <SearchBar value={searchTerm} onChange={setSearchTerm} />

      {filteredMenuData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gold-light rounded-lg shadow-sm">
          <TbClipboardOff className='text-gold-dark text-6xl'/>
          <p className="text-gray-500 text-xl font-medium mb-2">No menu items available</p>
        </div>
      ) : (
        <div className="space-y-10">
          {filteredMenuData.map((section) => (
            <div key={section.id} className="bg-white rounded-lg shadow-md border border-amber-100">
              <SectionHeader
                section={section}
                onEdit={handleSaveSection}
                onDelete={() => handleDeleteSection(section.id)}
              />
              
              {section.items.length > 0 ? (
                <div className="flex flex-col gap-3 p-4 bg-gold-dark">
                  {section.items.map(item => (
                    <MenuItemCard
                      key={item.id}
                      item={item}
                      onEdit={() => {
                        setEditingItem({ section_id: section.id, ...item });
                        setShowItemModal(true);
                      }}
                      onDelete={() => handleDeleteItem(item.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-8 px-6 text-center bg-amber-50/50">
                  <p className="text-gray-500 mb-4">No items in this section</p>
                  <button
                    className="inline-flex items-center text-amber-700 hover:text-amber-800 font-medium"
                    onClick={() => {
                      setEditingItem({ section_id: section.id });
                      setShowItemModal(true);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add Item to This Section
                  </button>
                </div>
              )}
              
              <div className="py-3 px-6 bg-gold-light border-t border-amber-100 flex justify-end">
                <button
                  className="text-amber-700 hover:text-amber-900 text-sm font-medium flex items-center"
                  onClick={() => {
                    setEditingItem({ section_id: section.id });
                    setShowItemModal(true);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add New Item
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ItemModal
        isOpen={showItemModal}
        item={editingItem}
        sections={sections}
        onClose={() => {
          setShowItemModal(false);
          setEditingItem(null);
        }}
        onSave={handleSaveItem}
      />

      <SectionModal
        isOpen={showSectionModal}
        section={editingSection}
        onClose={() => {
          setShowSectionModal(false);
          setEditingSection(null);
        }}
        onSave={handleSaveSection}
      />
    </div>
  );
};

export default MenuManagement;